import moment from "moment";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Component, ReactNode } from "react";
import { AppCard } from "../../components/ui";
import history from "../../history";
import Loader from "../../components/Loader";
import { SftpTransaction } from "../../models/Transactions";
import { sftpService } from "../../services/SFTPService";
import { dateFilterTemplate, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { ActionBodyTemplate } from "./TransactionFilterTemplates";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { utils } from "../../utils/utils";
import { toastService } from "../../services/toastService";
import { Button } from "primereact/button";
import { DataTableToolbar } from "../../components/ui";
import { ArrowRightCircle, ArrowLeftCircle } from "lucide-react";

interface SFTPTransactionsState {
    transactions: SftpTransaction[];
    isLoading: boolean;
    totalRecords: number;
    transactionDetailVisible: boolean;
    selectedTransaction: string | null;
}

class SFTPTransactions extends Component<any, SFTPTransactionsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: any) {
        super(props);

        this.state = {
            transactions: [],
            isLoading: false,
            totalRecords: 0,
            transactionDetailVisible: false,
            selectedTransaction: null,
        };
    }

    onActionButtonClick = async (rowData: SftpTransaction) => {
        this.setState({ isLoading: true });

        const contentResult = await sftpService.GetFileContent(rowData.fileName);

        this.setState({ isLoading: false });

        if (!contentResult.success) {
            toastService.error(contentResult.message);
            return;
        }

        this.setState({ transactionDetailVisible: true, selectedTransaction: contentResult.data });
    };

    renderIcon = (rowData: SftpTransaction) => {
        if (rowData.action == null) {
            return "";
        }

        return rowData.action.toLowerCase() === "in" ? (
            <ArrowRightCircle size={24} className="text-[#d92550]" />
        ) : (
            <ArrowLeftCircle size={24} className="text-[#3ac47d]" />
        );
    };

    hideTransactionDetails = () => {
        this.setState({ transactionDetailVisible: false, selectedTransaction: null });
    };

    renderTransactionDetail = () => {
        if (!this.state.transactionDetailVisible) {
            return "";
        }

        if (this.state.selectedTransaction == null) {
            return (
                <Dialog header="Transaction Details" visible={true} style={{ width: "70vw" }} onHide={() => this.hideTransactionDetails()}>
                    <Loader />
                </Dialog>
            );
        }

        return (
            <Dialog header="Transaction Details" visible={true} style={{ width: "70vw" }} onHide={() => this.hideTransactionDetails()}>
                <pre className="prettyprint">{this.state.selectedTransaction}</pre>
            </Dialog>
        );
    };

    loadTransactionsData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
        const transactionsResult = await sftpService.ListFiles(searchRequest);

        this.setState({ isLoading: false });

        if (!transactionsResult.success) {
            toastService.error(transactionsResult.message);
        }

        this.setState({ transactions: transactionsResult.data, totalRecords: transactionsResult.totalCount });
    };

    renderDate = (date: Date | null) => {
        if (date == null) {
            return <p></p>;
        }

        return <p>{moment.utc(date).local().format("DD/MM/YYYY HH:mm:ss")}</p>;
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => { history.replace({ pathname: "/sftp-files-exchanged" }); this.dataTable!.reset(); }} hasActiveFilters={hasActiveFilters} />;

    render(): ReactNode {
        return (
            <>
                <AppCard className="mb-3">
                    {this.renderTransactionDetail()}
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            header={this.renderHeader}
                            ref={(ref) => (this.dataTable = ref)}
                            value={this.state.transactions}
                            onChange={this.loadTransactionsData}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            defaultSortOrder={SortOrderType.Descending}
                            defaultSortField="fileTimeStamp"
                        >
                            <Column {...({ alwaysVisible: true } as any)} field="to" header="To" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column  {...({ alwaysVisible: true } as any)} field="from" header="From" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column {...({ alwaysVisible: true } as any)} field="fileName" header="File Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column field="user" header="User" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column header="Icon" body={(rowData: SftpTransaction) => this.renderIcon(rowData)} />
                            <Column field="action" header="Action" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column field="actionType" header="Action Type" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column
                                field="fileTimeStamp"
                                header="File TimeStamp"
                                body={(rowData: SftpTransaction) => this.renderDate(rowData.actionTimeStamp)}
                                dataType="date"
                                filterElement={dateFilterTemplate}
                                filterMatchModeOptions={numberFilterMatchModeOptions}
                            />
                            <Column field="transferStatus" header="Transfer Status" filterMatchModeOptions={stringFilterMatchModeOptions} />
                            <Column
                                field="actionTimeStamp"
                                header="TimeStamp"
                                body={(rowData: SftpTransaction) => this.renderDate(rowData.actionTimeStamp)}
                                dataType="date"
                                filterElement={dateFilterTemplate}
                                filterMatchModeOptions={numberFilterMatchModeOptions}
                            />
                            <Column
                                header="View Content"
                                body={(rowData: SftpTransaction) => <ActionBodyTemplate data={rowData} onClickAction={this.onActionButtonClick} />}
                            />
                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
            </>
        );
    }
}

export default SFTPTransactions;
